import {
  PinComponentSwitch,
  PinContext,
  PinScaleWrapper,
  pinSize,
  PinThemeContextProvider,
  useLinkedPinComponents,
  usePinResizeObserver,
  useSortPinComponents,
} from '@lessonup/pin-renderer';
import { PinTheme, TeacherPin } from '@lessonup/pins-shared';
import {
  FocusModeOverlay,
  FocusModeSpinner,
  useCurrentView,
  useFocusMode,
  useHandleMouseEventsForPlayer,
  useHiddenAnimatedComponents,
  usePlayerDispatcher,
} from '@lessonup/players-modern';
import { color, cssBase, isLeftMBClick, isRightMBClick, styled } from '@lessonup/ui-components';
import { clsx } from 'clsx';
import React, { useCallback, useRef } from 'react';
import { PlayerFAM } from '../../../components/PlayerFAM/PlayerFAM';
import { useGetSelection } from '../../hooks/useGetSelection';
import { usePinInteractionTracking } from '../../hooks/usePinInteractionTracking';
import { PinComponentSwitchRendererRealtimePlayerMemo } from './PinComponentSwitchRealtimePlayer/PinComponentSwitchRealtimePlayer';
import { PinSwitchRealtimePlayer } from './PinSwitchRealtimePlayer/PinSwitchRealtimePlayer';

export interface PinViewerRealtimePlayerProps {
  pin: TeacherPin;
  pinTheme: PinTheme;
  withFAM?: boolean;
}

/**
 * This will render a complete pin for the player
 * Only action that is supported is the move action for single components
 * Should be inside a UpdatePinComponentContextProvider if you want to persist the changes
 */
export const PinViewerRealtimePlayer: React.FC<PinViewerRealtimePlayerProps> = ({ pin, pinTheme, withFAM }) => {
  const dispatch = usePlayerDispatcher();
  const pinComponents = useSortPinComponents(pin.pinComponents);
  const pinWrapperRef = useRef<HTMLDivElement>(null);
  const focusMode = useFocusMode();
  const selectedPinComponentIds = useGetSelection('pinComponent');

  usePinInteractionTracking({ pin });

  const { scale, pinRendererOffset, resizeObserverInitialized } = usePinResizeObserver(pinWrapperRef);
  const { mutatingPinComponent, handleOnMouseDown, handleOnMouseUp } = useHandleMouseEventsForPlayer(
    scale,
    pinRendererOffset,
    pinComponents,
    pin
  );

  const { isLinkedToInactiveHotspot, isLinkedToHotspotList } = useLinkedPinComponents(pinComponents);
  const hiddenComponents = useHiddenAnimatedComponents();
  const currentView = useCurrentView();

  const handlePickOnMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isLeftMBClick(event)) {
        handleOnMouseDown(event, { action: 'select' });
      } else if (isRightMBClick(event)) {
        handleOnMouseDown(event, { action: 'select-pin-components', componentIds: [] });
      }
    },
    [handleOnMouseDown]
  );

  return (
    <StyledPinWrapper ref={pinWrapperRef} onMouseDown={handlePickOnMouseDown} data-testid="pin-content">
      <PinScaleWrapper scale={scale} resizeObserverInitialized={resizeObserverInitialized}>
        <StyledPin style={{ transform: `scale(${scale})` }}>
          <PinThemeContextProvider pinType={pin.type} pinTheme={pinTheme}>
            <PinContext.Provider value={pin}>
              <FocusModeOverlay
                focusMode={focusMode}
                renderSpinner={(fm) => (
                  <FocusModeSpinner
                    focusMode={fm}
                    onSpinningChange={(isSpinning) =>
                      dispatch({
                        type: 'setFocusMode',
                        focusMode: { ...fm, isSpinning },
                      })
                    }
                  />
                )}
              >
                <PinSwitchRealtimePlayer pin={pin} pinScale={scale}>
                  {pinComponents.map((pinComponent) => {
                    const latestPinComponent =
                      mutatingPinComponent?.id === pinComponent.id ? mutatingPinComponent : pinComponent;
                    const linkedComponent = isLinkedToInactiveHotspot(pinComponent.id);
                    const isHiddenByAnimation = hiddenComponents.has(pinComponent.id);

                    const animationStep =
                      currentView?.type === 'NON_INTERACTIVE' &&
                      currentView.lastPlayedAnimationStep?.componentId === pinComponent.id
                        ? currentView.lastPlayedAnimationStep.step
                        : undefined;

                    return (
                      <PinComponentSwitch
                        pin={pin}
                        key={pinComponent.id}
                        pinComponent={latestPinComponent}
                        onMouseDown={handleOnMouseDown}
                        editable={false}
                        active={false}
                        isStatic={!pinComponent.settings.movableInInteractiveViewer}
                        isHidden={linkedComponent || isHiddenByAnimation}
                        animationStep={animationStep}
                        Renderer={PinComponentSwitchRendererRealtimePlayerMemo}
                        scale={scale}
                        className={clsx({
                          [zIndexSelected]:
                            selectedPinComponentIds.includes(pinComponent.id) ||
                            isLinkedToHotspotList(pinComponent.id, selectedPinComponentIds),
                        })}
                      />
                    );
                  })}
                </PinSwitchRealtimePlayer>
              </FocusModeOverlay>
            </PinContext.Provider>
          </PinThemeContextProvider>
        </StyledPin>
        {withFAM && (
          <PlayerFAM
            scale={scale}
            pinComponents={pin.pinComponents}
            mutatingPinComponent={mutatingPinComponent}
            onMouseUp={handleOnMouseUp}
            pinRendererOffset={pinRendererOffset}
          />
        )}
      </PinScaleWrapper>
    </StyledPinWrapper>
  );
};

const StyledPin = styled.div`
  width: ${pinSize.width}px;
  height: ${pinSize.height}px;
  transform-origin: left top;
  position: relative;
  overflow: hidden;
  outline: ${color.neutral.outline.background} solid 1px;
`;

// There is currently no better way to target the pin switch, since it does not expose a common html element
/**
 * This will hide the pin when the focus mode is active.
 * Assumption is the pin will always be the first child
 */

const zIndexSelected = cssBase`
  z-index: 1;
`;

const StyledPinWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
